/**
 * Web push (FCM) — permission, token storage, foreground listener.
 * Send happens in Cloud Functions, never from the client.
 */

import {
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { app, db } from '../../firebase';
import { trackUxEvent } from '../uxEvents';

export const FCM_TOKENS_SUBCOLLECTION = 'fcmTokens';
export const PUSH_SW_PATH = '/firebase-messaging-sw.js';
export const PUSH_ENABLED_STORAGE_KEY = 'm2b_web_push_on';

export const PUSH_STATUS = Object.freeze({
  UNSUPPORTED: 'unsupported',
  NEEDS_HOMESCREEN: 'needs-homescreen',
  MISSING_VAPID: 'missing-vapid',
  DEFAULT: 'default',
  GRANTED: 'granted',
  DENIED: 'denied',
});

let messagingInstance = null;

export function getFcmVapidKey() {
  return String(process.env.REACT_APP_FCM_VAPID_KEY || '').trim();
}

/** Firestore-safe doc id from an FCM token (tokens may contain `/`). */
export function fcmTokenDocId(token) {
  const raw = String(token || '').trim();
  if (!raw) throw new Error('Missing push token.');
  return raw.replace(/\//g, '_');
}

export function isWebPushApiAvailable(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win) return false;
  const nav = win.navigator;
  return Boolean(
    nav &&
      'serviceWorker' in nav &&
      'PushManager' in win &&
      'Notification' in win
  );
}

export function isIosDevice(nav = typeof navigator !== 'undefined' ? navigator : undefined) {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1;
}

export function isStandaloneDisplay(win = typeof window !== 'undefined' ? window : undefined) {
  if (!win) return false;
  const standalone = Boolean(win.navigator && win.navigator.standalone === true);
  const media =
    typeof win.matchMedia === 'function' && win.matchMedia('(display-mode: standalone)').matches;
  return standalone || Boolean(media);
}

export function needsHomeScreenInstall(
  win = typeof window !== 'undefined' ? window : undefined
) {
  if (!win) return false;
  return isIosDevice(win.navigator) && !isStandaloneDisplay(win);
}

export function shouldSavePushToken(permission) {
  return permission === 'granted';
}

export function isPushEnabledLocally(
  storage = typeof localStorage !== 'undefined' ? localStorage : undefined
) {
  if (!storage) return false;
  try {
    return storage.getItem(PUSH_ENABLED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPushEnabledLocally(
  on,
  storage = typeof localStorage !== 'undefined' ? localStorage : undefined
) {
  if (!storage) return;
  try {
    if (on) storage.setItem(PUSH_ENABLED_STORAGE_KEY, '1');
    else storage.removeItem(PUSH_ENABLED_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function getPushStatus({
  win = typeof window !== 'undefined' ? window : undefined,
  vapidKey = getFcmVapidKey(),
} = {}) {
  if (!win) return PUSH_STATUS.UNSUPPORTED;
  if (needsHomeScreenInstall(win)) return PUSH_STATUS.NEEDS_HOMESCREEN;
  if (!isWebPushApiAvailable(win)) return PUSH_STATUS.UNSUPPORTED;
  if (!vapidKey) return PUSH_STATUS.MISSING_VAPID;
  const perm = win.Notification && win.Notification.permission;
  if (perm === 'granted') return PUSH_STATUS.GRANTED;
  if (perm === 'denied') return PUSH_STATUS.DENIED;
  return PUSH_STATUS.DEFAULT;
}

async function getMessagingInstance() {
  if (!app) return null;
  if (!(await isSupported())) return null;
  if (!messagingInstance) messagingInstance = getMessaging(app);
  return messagingInstance;
}

export async function registerPushServiceWorker(
  nav = typeof navigator !== 'undefined' ? navigator : undefined
) {
  if (!nav || !('serviceWorker' in nav)) return null;
  return nav.serviceWorker.register(PUSH_SW_PATH);
}

async function saveTokenDoc(uid, token) {
  const id = fcmTokenDocId(token);
  const ua =
    typeof navigator !== 'undefined' && navigator.userAgent
      ? String(navigator.userAgent).slice(0, 200)
      : '';
  await setDoc(doc(db, 'users', uid, FCM_TOKENS_SUBCOLLECTION, id), {
    token,
    userAgent: ua,
    updatedAt: serverTimestamp(),
  });
}

async function deleteTokenDoc(uid, token) {
  if (!uid || !token) return;
  await deleteDoc(doc(db, 'users', uid, FCM_TOKENS_SUBCOLLECTION, fcmTokenDocId(token)));
}

/**
 * Refresh this device token when permission is already granted and the user opted in.
 */
export async function syncWebPushToken(uid) {
  if (!uid || !isPushEnabledLocally()) return null;
  if (getPushStatus() !== PUSH_STATUS.GRANTED) return null;
  const vapidKey = getFcmVapidKey();
  if (!vapidKey) return null;
  const messaging = await getMessagingInstance();
  if (!messaging) return null;
  const registration = await registerPushServiceWorker();
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration || undefined,
  });
  if (!token) return null;
  await saveTokenDoc(uid, token);
  return token;
}

export async function enableWebPush(uid) {
  if (!uid) throw new Error('Missing user.');
  const status = getPushStatus();
  if (status === PUSH_STATUS.UNSUPPORTED) {
    throw new Error('This browser cannot show lock-screen alerts.');
  }
  if (status === PUSH_STATUS.MISSING_VAPID) {
    throw new Error('Phone alerts are not configured yet.');
  }
  if (status === PUSH_STATUS.NEEDS_HOMESCREEN) {
    throw new Error('Add MapToBe to your Home Screen first.');
  }
  if (status === PUSH_STATUS.DENIED) {
    throw new Error('Alerts are blocked in the browser.');
  }

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    trackUxEvent({ name: 'push_permission_deny' });
    throw new Error('Alerts are blocked in the browser.');
  }
  if (!shouldSavePushToken(permission)) {
    return { permission, token: null };
  }

  const vapidKey = getFcmVapidKey();
  const messaging = await getMessagingInstance();
  if (!messaging || !vapidKey) {
    throw new Error('Phone alerts are not configured yet.');
  }
  const registration = await registerPushServiceWorker();
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration || undefined,
  });
  if (!token) throw new Error('Could not enable phone alerts.');
  await saveTokenDoc(uid, token);
  setPushEnabledLocally(true);
  trackUxEvent({ name: 'push_permission_grant' });
  return { permission: 'granted', token };
}

export async function disableWebPush(uid) {
  if (!uid) throw new Error('Missing user.');
  const messaging = await getMessagingInstance();
  let token = null;
  if (messaging) {
    try {
      token = await getToken(messaging, { vapidKey: getFcmVapidKey() });
    } catch {
      token = null;
    }
    try {
      await deleteToken(messaging);
    } catch {
      /* already gone */
    }
  }
  if (token) {
    try {
      await deleteTokenDoc(uid, token);
    } catch {
      /* rules / missing doc */
    }
  }
  setPushEnabledLocally(false);
}

/**
 * Foreground FCM — subscribe so data messages do not spawn a second banner.
 * The header bell already updates live.
 */
export function listenForegroundMessages(onPayload) {
  let unsub = () => {};
  let cancelled = false;
  getMessagingInstance()
    .then((messaging) => {
      if (cancelled || !messaging) return;
      unsub = onMessage(messaging, (payload) => {
        if (typeof onPayload === 'function') onPayload(payload);
      });
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    unsub();
  };
}
